import React, { useRef, useEffect, useState, useCallback } from 'react';

function ClickHeatmap({ data, lpUrl: initialLpUrl }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const wrapperRef = useRef(null);
  const [radius, setRadius] = useState(30);
  const [intensity, setIntensity] = useState(0.6);
  const [originalHeight, setOriginalHeight] = useState(2000);
  const [lpUrl, setLpUrl] = useState(initialLpUrl || '/api/lp');

  const allClicks = data.flatMap(d => (d.clicks || []).map(c => ({
    x: c.x,
    y: c.y,
    pageWidth: d.page ? d.page.width : 1440,
    pageHeight: d.page ? d.page.height : 2000,
  })));

  // Use the original page width from tracking data
  const originalPageWidth = allClicks.length > 0
    ? Math.max(...allClicks.map(c => c.pageWidth))
    : 1440;

  const drawHeatmap = useCallback(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper || allClicks.length === 0) return;

    // Canvas matches the original page dimensions exactly (no scaling)
    canvas.width = originalPageWidth;
    canvas.height = originalHeight;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Create heatmap using alpha compositing
    const heatCanvas = document.createElement('canvas');
    heatCanvas.width = canvas.width;
    heatCanvas.height = canvas.height;
    const heatCtx = heatCanvas.getContext('2d');

    allClicks.forEach(click => {
      // Use original coordinates directly — no scaling needed
      const x = click.x;
      const y = click.y;
      if (y > canvas.height || x > canvas.width) return;

      const gradient = heatCtx.createRadialGradient(x, y, 0, x, y, radius);
      gradient.addColorStop(0, `rgba(255, 0, 0, ${intensity})`);
      gradient.addColorStop(1, 'rgba(255, 0, 0, 0)');
      heatCtx.fillStyle = gradient;
      heatCtx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
    });

    // Colorize heatmap
    const imageData = heatCtx.getImageData(0, 0, heatCanvas.width, heatCanvas.height);
    const pixels = imageData.data;

    for (let i = 0; i < pixels.length; i += 4) {
      const alpha = pixels[i + 3];
      if (alpha === 0) continue;
      const val = alpha / 255;
      let r, g, b;
      if (val < 0.25) {
        r = 0; g = Math.round(val * 4 * 255); b = 255;
      } else if (val < 0.5) {
        r = 0; g = 255; b = Math.round((1 - (val - 0.25) * 4) * 255);
      } else if (val < 0.75) {
        r = Math.round((val - 0.5) * 4 * 255); g = 255; b = 0;
      } else {
        r = 255; g = Math.round((1 - (val - 0.75) * 4) * 255); b = 0;
      }
      pixels[i] = r;
      pixels[i + 1] = g;
      pixels[i + 2] = b;
      pixels[i + 3] = Math.min(alpha * 2, 180);
    }

    heatCtx.putImageData(imageData, 0, 0);
    ctx.drawImage(heatCanvas, 0, 0);

    // Draw click dots
    allClicks.forEach(click => {
      const x = click.x;
      const y = click.y;
      if (y > canvas.height || x > canvas.width) return;
      ctx.beginPath();
      ctx.arc(x, y, 4, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.lineWidth = 1;
      ctx.stroke();
    });

    // Info
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.fillRect(0, 0, 130, 28);
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 13px sans-serif';
    ctx.fillText(`${allClicks.length} クリック`, 10, 18);
  }, [allClicks, radius, intensity, originalHeight, originalPageWidth]);

  useEffect(() => {
    drawHeatmap();
  }, [drawHeatmap]);

  // Get iframe's actual content height
  const handleIframeLoad = () => {
    try {
      const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
      const height = doc.documentElement.scrollHeight;
      setOriginalHeight(height);
    } catch {
      setOriginalHeight(3000);
    }
  };

  if (allClicks.length === 0) {
    return (
      <div className="empty-state">
        <div className="icon">🖱️</div>
        <h3>クリックデータがありません</h3>
        <p>LPにトラッキングスクリプトを埋め込んでクリックデータを収集してください</p>
        <code>&lt;script src="/api/tracker/heatmap-tracker.js"&gt;&lt;/script&gt;</code>
      </div>
    );
  }

  return (
    <div>
      <div className="heatmap-controls">
        <label>
          半径: {radius}px
          <input type="range" min="10" max="80" value={radius} onChange={e => setRadius(Number(e.target.value))} />
        </label>
        <label>
          強度: {intensity}
          <input type="range" min="0.1" max="1" step="0.1" value={intensity} onChange={e => setIntensity(Number(e.target.value))} />
        </label>
        <label>
          LP URL:
          <input
            type="text"
            value={lpUrl}
            onChange={e => setLpUrl(e.target.value)}
            style={{
              marginLeft: 8,
              padding: '4px 8px',
              background: '#0d1117',
              border: '1px solid #2d333b',
              borderRadius: 6,
              color: '#e1e4e8',
              fontSize: 13,
              width: 300,
            }}
          />
        </label>
      </div>
      <div
        ref={containerRef}
        style={{
          position: 'relative',
          width: '100%',
          overflow: 'auto',
          borderRadius: 8,
          border: '1px solid #2d333b',
        }}
      >
        {/*
          Wrapper renders at the original page width.
          iframe inside also renders at that width so layout matches exactly.
          CSS transform scales the whole thing to fit the container.
        */}
        <div
          ref={wrapperRef}
          style={{
            position: 'relative',
            width: originalPageWidth,
            height: originalHeight,
            transformOrigin: 'top left',
            transform: `scale(var(--heatmap-scale, 1))`,
          }}
        >
          <iframe
            ref={iframeRef}
            src={lpUrl}
            title="LP Preview"
            onLoad={handleIframeLoad}
            style={{
              width: originalPageWidth,
              height: originalHeight,
              border: 'none',
              pointerEvents: 'none',
            }}
          />
          <canvas
            ref={canvasRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: originalPageWidth,
              height: originalHeight,
              pointerEvents: 'none',
            }}
          />
        </div>
      </div>
      {/* Inline script to compute scale dynamically */}
      <ScaleUpdater containerRef={containerRef} originalWidth={originalPageWidth} originalHeight={originalHeight} />
    </div>
  );
}

// Updates scale when window resizes (observe window, not container to avoid loop)
function ScaleUpdater({ containerRef, originalWidth, originalHeight }) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let lastWidth = 0;
    const updateScale = () => {
      const displayWidth = container.parentElement.clientWidth;
      if (displayWidth === lastWidth) return;
      lastWidth = displayWidth;

      const scale = displayWidth / originalWidth;
      const wrapper = container.firstChild;
      if (wrapper) {
        wrapper.style.transform = `scale(${scale})`;
      }
      container.style.height = `${originalHeight * scale}px`;
      container.style.overflow = 'hidden';
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [containerRef, originalWidth, originalHeight]);

  return null;
}

export default ClickHeatmap;
